import React, { useEffect, useState } from 'react';
import { myEarnings, myPayouts, requestPayout, resolveFileUrl } from '../lib/api';
import { DollarSign, Wallet, Clock, CheckCircle2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Earnings() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: '', method: 'paypal', destination: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([myEarnings(), myPayouts()]);
      setData(d); setPayouts(p);
    } catch (err) { toast.error('Failed to load earnings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (!form.destination.trim()) return toast.error('Enter your payout destination');
    setSubmitting(true);
    try {
      await requestPayout({ amount: amt, method: form.method, destination: form.destination });
      toast.success('Payout requested');
      setForm({ amount: '', method: 'paypal', destination: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  if (loading || !data) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="earnings-page">
      <div className="flex items-center gap-2 text-[#22d3ee]"><DollarSign className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Creator revenue</span></div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-2 leading-none">Earnings & payouts</h1>
      <p className="text-white/60 text-[14px] mt-1">Ad revenue accrued on your monetized videos. Request payouts at any time.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat ic={Wallet}        color="#22d3ee" label="Available"  value={`$${data.available.toFixed(2)}`} testid="earnings-available" />
        <Stat ic={DollarSign}    color="#34d399" label="Lifetime"   value={`$${data.total.toFixed(2)}`} />
        <Stat ic={Clock}         color="#ffcf52" label="Pending"    value={`$${data.pending.toFixed(2)}`} />
        <Stat ic={CheckCircle2}  color="#a78bfa" label="Paid out"   value={`$${data.paid.toFixed(2)}`} />
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-10">
        <div className="card-shell overflow-hidden">
          <div className="p-5"><h3 className="row-title text-white">Top earning videos</h3></div>
          {data.by_video.length === 0 ? (
            <div className="p-8 text-center text-white/50 text-[13.5px]">No earnings yet. Enable monetization on videos in Creator Studio.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.by_video.slice(0, 12).map(v => (
                <li key={v.video_id} className="flex items-center gap-3 px-5 py-3">
                  {v.thumb ? <img src={resolveFileUrl(v.thumb)} alt="" className="w-16 h-10 rounded object-cover" /> : <div className="w-16 h-10 rounded bg-white/10" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold truncate">{v.title || v.video_id}</div>
                    <div className="text-[12px] text-white/55">{v.events} ad events</div>
                  </div>
                  <div className="font-display text-[18px]">${v.amount.toFixed(3)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-shell p-5">
          <h3 className="row-title text-white">Request payout</h3>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder={`Amount (max $${data.available.toFixed(2)})`} className="input-field w-full" data-testid="payout-amount" />
            <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} className="input-field w-full">
              <option value="paypal">PayPal</option>
              <option value="bank">Bank transfer</option>
              <option value="crypto">Crypto (USDT)</option>
            </select>
            <input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder={form.method === 'paypal' ? 'PayPal email' : form.method === 'bank' ? 'IBAN / account' : 'Wallet address'} className="input-field w-full" data-testid="payout-destination" />
            <button disabled={submitting || data.available <= 0} className="btn-primary w-full disabled:opacity-50" data-testid="payout-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request payout
            </button>
            <p className="text-[11.5px] text-white/45">Payouts are reviewed by the platform owner. Typical turnaround is 3-5 business days.</p>
          </form>
        </div>
      </div>

      <div className="card-shell mt-10 overflow-hidden">
        <div className="p-5"><h3 className="row-title text-white">Payout history</h3></div>
        {payouts.length === 0 ? (
          <div className="p-8 text-center text-white/50 text-[13.5px]">No payouts requested yet.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead className="bg-white/[0.03] text-white/55">
              <tr>
                <th className="text-left px-5 py-2">Date</th>
                <th className="text-left px-3 py-2">Method</th>
                <th className="text-left px-3 py-2">Destination</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-5 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="px-5 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-3 capitalize">{p.method}</td>
                  <td className="px-3 py-3 text-white/70 truncate max-w-[260px]">{p.destination}</td>
                  <td className="px-3 py-3 text-right font-semibold">${p.amount.toFixed(2)}</td>
                  <td className="px-5 py-3"><PayoutBadge s={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PayoutBadge({ s }) {
  const map = {
    pending:  { c: '#ffcf52', t: 'Pending' },
    approved: { c: '#22d3ee', t: 'Approved' },
    paid:     { c: '#34d399', t: 'Paid' },
    rejected: { c: '#ff6b8a', t: 'Rejected' },
  };
  const m = map[s] || { c: '#94a3b8', t: s };
  return <span className="text-[11.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ background: m.c + '22', color: m.c, border: `1px solid ${m.c}55` }}>{m.t}</span>;
}

function Stat({ ic: Ic, color, label, value, testid }) {
  return (
    <div className="card-shell p-5" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: color + '22', border: '1px solid ' + color + '55' }}>
        <Ic className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-display text-[30px] mt-3 leading-none">{value}</div>
      <div className="text-[12px] text-white/55 mt-1">{label}</div>
    </div>
  );
}
